/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible, setModalQuotationVisible } from "@store/slices/appSlice";
import { setMotorbikeInformation, setNewConditionLoan } from "@store/slices/registerSlice";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import { MotorbikeCatalogItem, TaxSystem } from "@interfaces/catalogs";
import { useLazyGetMotorbikeCatalogQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import {
  useLazyGetConditionsByIdQuery,
  usePostConditionMutation,
  usePutMotorbikeDetailMutation,
} from "@api/applications";
import { QuotationOferta } from "@components/QuotationOferta";
import CenteredLoader from "@components/CenteredLoader";
import CloseIcon from "@mui/icons-material/Close";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CardInformacionMoto from "./CardInformacionMoto";
import { TAX_SYSTEMS_CODES } from "@helpers/constantes";
import { calculateAmountMonthly } from "@helpers/funciones";
import * as Sentry from "@sentry/react";

interface NewLoanCondition {
  has_change: boolean;
  amount: number;
  term: number;
  monthlyPayment: number;
}

interface CotizadorDefaultProps {
  condition: LoanCondiciones | null;
  taxSystems: TaxSystem | null;
  handleOnClickClose: () => void;
}

interface CotizadorMotoProps {
  motorbikeCatalog: MotorbikeCatalogItem[];
  taxSystems: TaxSystem | null;
  handleOnClickClose: () => void;
}

interface CloseButtonProps {
  isSaving: boolean;
  handleOnClickClose: () => void;
}

const ModalCloseButton = ({ isSaving, handleOnClickClose }: CloseButtonProps) => {
  return (
    <IconButton
      aria-label="Cerrar"
      onClick={handleOnClickClose}
      disabled={isSaving}
      size="small"
      sx={{
        position: "absolute",
        right: 8,
        top: "50%",
        transform: "translateY(-50%)",
        color: "#002652",
      }}
    >
      <CloseIcon />
    </IconButton>
  );
};

const CotizadorDefault = ({ condition, taxSystems, handleOnClickClose }: CotizadorDefaultProps) => {
  // hooks
  const dispatch = useAppDispatch();

  // redux hooks
  const loanApplication = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [triggerPostCondition] = usePostConditionMutation();

  // state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [newConditions, setNewConditions] = useState<NewLoanCondition>({
    has_change: false,
    amount: 0,
    term: 0,
    monthlyPayment: 0,
  });

  const handleOnUpdateValues = (amount: number, term: number, monthlyPayment: number) => {
    if (!condition) return;

    const has_change_amount = condition.selected_amount !== amount;
    const has_change_term = condition.selected_term !== term;

    setNewConditions({
      has_change: has_change_amount || has_change_term,
      amount: amount,
      term: term,
      monthlyPayment: monthlyPayment,
    });
  };

  const handleOnClickUpdateConditionLoan = async () => {
    try {
      setIsSaving(true);
      await triggerPostCondition({
        loan_application: loanApplication.id,
        loan_amount: newConditions.amount,
        requested_term: newConditions.term,
        selected_amount: newConditions.amount,
        selected_term: newConditions.term,
        requested_amount: newConditions.amount,
        user_name: `TOFU - ${accountUser.email}`,
        user_id: String(accountUser.id),
      }).unwrap();

      dispatch(
        setNewConditionLoan({
          approved_amount: loanApplication.approved_amount,
          approved_term: loanApplication.approved_term,
          interest_rate: loanApplication.interest_rate,
          loanAmount: newConditions.amount,
          loanTerm: newConditions.term,
          amountMonthly: newConditions.monthlyPayment,
        }),
      );
      dispatch(setModalQuotationVisible(false));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "CotizadorDefault",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ position: "relative", pr: 6, pt: 2 }}>
        <Typography variant="body1" color={"#002652"} fontWeight={600} textAlign={"center"}>
          Modifica tu cotización
        </Typography>
        <ModalCloseButton isSaving={isSaving} handleOnClickClose={handleOnClickClose} />
      </DialogTitle>
      <DialogContent>
        <Box mx={2}>
          <QuotationOferta condition={condition} taxSystems={taxSystems} onUpdateValues={handleOnUpdateValues} />

          <Grid container columnSpacing={1} my={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="blue_outlined"
                sx={{ padding: "0", height: "100%" }}
                onClick={handleOnClickClose}
              >
                Regresar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="default"
                size="medium"
                onClick={handleOnClickUpdateConditionLoan}
                disabled={isSaving || !newConditions.has_change}
              >
                Continuar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </>
  );
};

const CotizadorMoto = ({ motorbikeCatalog, taxSystems, handleOnClickClose }: CotizadorMotoProps) => {
  // hooks
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // redux hooks
  const loanApplication = useSelector((state: RootState) => state.register.application);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [putMotorbikeDetail] = usePutMotorbikeDetailMutation();
  const [triggerPostCondition] = usePostConditionMutation();

  // state
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleScroll = () => {
    if (!scrollContainerRef.current || motorbikeCatalog.length === 0) return;
    const container = scrollContainerRef.current;
    const pageWidth = container.clientWidth;
    if (pageWidth === 0) return;
    const index = Math.round(container.scrollLeft / pageWidth);
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    setActiveIndex(safeIndex);
  };

  const scrollToMotoIndex = (index: number) => {
    if (!scrollContainerRef.current || motorbikeCatalog.length === 0) return;
    const container = scrollContainerRef.current;
    const pageWidth = container.clientWidth;
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    container.scrollTo({ left: safeIndex * pageWidth, behavior: "smooth" });
  };

  const handleOnSelectMotorbike = async (motorbikeItem: MotorbikeCatalogItem) => {
    try {
      if (!motorbikeItem?.data || !motorbikeInfo?.motorbikeDetailId || !taxSystems?.default_term) {
        return false;
      }

      if (!taxSystems?.default_term) {
        throw new Error("No se ha configurado el plazo por defecto");
      }

      setIsSaving(true);

      await putMotorbikeDetail({
        solicitudId: loanApplication.id,
        motorbikeDetailId: motorbikeInfo.motorbikeDetailId,
        body: {
          motorbike_catalog_id: motorbikeItem.id,
          details: {
            model: motorbikeItem.data.model,
            price: motorbikeItem.data.price,
            down_payment: motorbikeItem.data.down_payment,
            daily_payment: motorbikeItem.data.daily_payment,
          },
        },
      }).unwrap();

      await triggerPostCondition({
        loan_application: loanApplication.id,
        loan_amount: motorbikeItem.data.price,
        requested_term: taxSystems?.default_term,
        selected_amount: motorbikeItem.data.price,
        selected_term: taxSystems?.default_term,
        requested_amount: motorbikeItem.data.price,
        user_name: `TOFU - ${accountUser.email}`,
        user_id: String(accountUser.id),
      }).unwrap();

      dispatch(
        setNewConditionLoan({
          approved_amount: loanApplication.approved_amount,
          approved_term: loanApplication.approved_term,
          interest_rate: loanApplication.interest_rate,
          loanAmount: motorbikeItem.data.price,
          loanTerm: taxSystems?.default_term,
          amountMonthly: calculateAmountMonthly(motorbikeItem.data.price, taxSystems?.default_term),
        }),
      );
      dispatch(
        setMotorbikeInformation({
          motorbikeCatalogId: motorbikeItem.id,
        }),
      );
      dispatch(setModalQuotationVisible(false));

      handleOnClickClose();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "CotizadorMoto",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollLeft = 0;
    setActiveIndex(0);
  }, [motorbikeCatalog]);

  return (
    <>
      <DialogTitle sx={{ position: "relative", pr: 6, pt: 2 }}>
        <Typography variant="body1" color={"#002652"} fontWeight={600} textAlign={"center"}>
          Elegir otra moto
        </Typography>
        <ModalCloseButton isSaving={isSaving} handleOnClickClose={handleOnClickClose} />
      </DialogTitle>
      <DialogContent>
        <Box width={isMobileOrTablet ? "100%" : "380px"} my={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              aria-label="Moto anterior"
              onClick={() => scrollToMotoIndex(activeIndex - 1)}
              disabled={activeIndex === 0}
              color="primary"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <KeyboardArrowLeft fontSize="large" />
            </IconButton>

            <Box
              ref={scrollContainerRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {motorbikeCatalog.map((moto) => (
                <Box
                  key={moto.id}
                  sx={{
                    flex: "0 0 100%",
                    minWidth: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    justifyContent: "center",
                    scrollSnapAlign: "start",
                    scrollSnapStop: "always",
                    pb: 1,
                  }}
                >
                  <CardInformacionMoto
                    item={moto}
                    onSelect={handleOnSelectMotorbike}
                    size="small"
                    disabled={isSaving}
                    isSelected={motorbikeInfo?.motorbikeCatalogId === moto.id}
                  />
                </Box>
              ))}
            </Box>

            <IconButton
              aria-label="Moto siguiente"
              onClick={() => scrollToMotoIndex(activeIndex + 1)}
              disabled={activeIndex >= motorbikeCatalog.length - 1}
              color="primary"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <KeyboardArrowRight fontSize="large" />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="center" gap={1} mt={1.5}>
            {Array.from({ length: motorbikeCatalog.length }).map((_, index) => (
              <Box
                key={index}
                role="button"
                tabIndex={0}
                aria-label={`Ir a moto ${index + 1}`}
                onClick={() => scrollToMotoIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    scrollToMotoIndex(index);
                  }
                }}
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: index === activeIndex ? "primary.light" : "#C9D8F5",
                  transition: "all .2s ease",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
    </>
  );
};

export const QuotationModal = () => {
  // hooks
  const dispatch = useAppDispatch();

  // redux hooks
  const modalState = useSelector((state: RootState) => state.app.modal_quotation);
  const loanApplication = useSelector((state: RootState) => state.register.application);

  // api hooks
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getMotorbikeCatalog] = useLazyGetMotorbikeCatalogQuery();

  // state
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [taxSystems, setTaxSystems] = useState<TaxSystem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [motorbikeCatalog, setMotorbikeCatalog] = useState<MotorbikeCatalogItem[]>([]);

  const handleOnClickClose = () => {
    dispatch(setModalQuotationVisible(false));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (modalState?.visible === false) return;

        setIsLoading(true);

        const [conditions, taxSystems] = await Promise.all([
          getConditions(loanApplication.id).unwrap(),
          getTaxSystems(loanApplication.type).unwrap(),
        ]);

        if (loanApplication.type === TAX_SYSTEMS_CODES.CREDIMOTO_PF) {
          const motorbikeCatalog = await getMotorbikeCatalog().unwrap();
          setMotorbikeCatalog(motorbikeCatalog);
        }

        const activeCondition = conditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
          setTaxSystems(taxSystems);
        }

        console.log("application: ", loanApplication);
      } catch (error) {
        console.log("Ha ocurrido un error: ");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [modalState?.visible]);

  if (isLoading) {
    return (
      <Dialog open={modalState?.visible} maxWidth={"sm"}>
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} width={"280px"} height={"400px"}>
          <CenteredLoader />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={modalState?.visible} maxWidth={"sm"}>
      {loanApplication.type === TAX_SYSTEMS_CODES.CREDIMOTO_PF ? (
        <CotizadorMoto
          motorbikeCatalog={motorbikeCatalog}
          taxSystems={taxSystems}
          handleOnClickClose={handleOnClickClose}
        />
      ) : (
        <CotizadorDefault condition={condition} taxSystems={taxSystems} handleOnClickClose={handleOnClickClose} />
      )}
    </Dialog>
  );
};
